"use client"

import { Component, type ErrorInfo, type ReactNode } from "react"
import { datadogRum } from "@datadog/browser-rum"

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  resetKeys?: readonly unknown[]
  /** Identifies which boundary caught the error, for Datadog filtering. */
  boundaryName?: string
  /** Which tool crashed, for boundaries that wrap many tool types; included in the Datadog error report. */
  toolName?: string
}

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

function haveResetKeysChanged(
  previousKeys: readonly unknown[] | undefined,
  nextKeys: readonly unknown[] | undefined
) {
  if (previousKeys === nextKeys) return false
  if (!previousKeys || !nextKeys || previousKeys.length !== nextKeys.length) {
    return true
  }
  return previousKeys.some((key, index) => key !== nextKeys[index])
}

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    try {
      datadogRum.addError(error, {
        boundaryName: this.props.boundaryName ?? "unnamed",
        componentStack: errorInfo.componentStack,
        toolName: this.props.toolName,
      })
    } catch (telemetryError) {
      // Best-effort telemetry only.
      void telemetryError
    }
  }

  componentDidUpdate(previousProps: ErrorBoundaryProps) {
    if (
      this.state.hasError &&
      haveResetKeysChanged(previousProps.resetKeys, this.props.resetKeys)
    ) {
      this.setState({ hasError: false })
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 p-4">
          <div className="text-center">
            <h2 className="text-content-primary mb-2 text-xl font-semibold">
              Something went wrong
            </h2>
            <p className="text-content-secondary">
              An error occurred while rendering this content. Our team has been
              notified and will fix the issue as soon as possible.
            </p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
